import React from "react";
import { HbrSpinner, HbrCard, HbrBarChart } from "@harbor/elements-react-standalone";
import { useMoodleFeedbackResponsesAnalysis } from '../../../../hooks/useMoodleHooks';
import { decodeHtmlEntities } from '../../../../support/utils';
import StbLoader from '../../../security-toolbox-ui/StbLoader';

interface EventFeedbackAnalysisProps {
  feedbackId: number;
  groupId: number;
}

const chartColorSchemes = [
  ["#1D69CC", "#2774D9", "#45991F", "#BD7202", "#CC2D37", "#656C75", "#FBB14E"], // interact, info, success, warning, danger, neutral, yellow
  ["#45991F", "#2774D9", "#656C75", "#CC2D37", "#1D69CC", "#FBB14E", "#BD7202"],
  ["#CC2D37", "#656C75", "#2774D9", "#1D69CC", "#45991F", "#BD7202", "#FBB14E"],
];

// Bar chart config builder
function buildHbrBarChartConfig(
  questionName: string,
  answerMap: Record<string, { users: string[]; count: number }>,
  totalAttempts: number,
  colorScheme: string[]
) {
  const data = (Object.entries(answerMap) as [string, { users: string[]; count: number }][])
    .filter(([a]) => a)
    .map(([answer, info]) => ({
      name: decodeHtmlEntities(answer),
      value: info.count,
      users: info.users,
      tooltip: `${info.count} response${info.count !== 1 ? "s" : ""} (${((info.count/totalAttempts)*100).toFixed(1)}%)\n${info.users.join(", ")}`,
    }));

  return {
    layout: "horizontal",
    keys: ["value"],
    margin: { top: 20, right: 20, bottom: 20, left: 20 },
    data: data,
    enableLabel: true,
    labelSkipHeight: 0,
    labelSkipWidth: 0,
    axisLeft: { truncateTickAt: 10, tickPadding: 100 },
    indexBy: "name",
    legends: [],
    colorBy: "id",
    colors: colorScheme,
    tooltip: (bar) =>
      `${bar.data?.tooltip || `${bar.data.name}: ${bar.formattedValue}`}`,
  };
}

export function EventFeedbackAnalysis({
                                        feedbackId,
                                        groupId,
                                      }: EventFeedbackAnalysisProps) {
  const { data, isLoading, error } = useMoodleFeedbackResponsesAnalysis(
    feedbackId,
    groupId
  );

  if (!feedbackId || !groupId) return null;
  if (isLoading) return <StbLoader isLoading={isLoading} />;
  if (error)
    return (
      <div style={{ color: "red" }}>
        Error loading feedback analysis: {error.message || String(error)}
      </div>
    );
  if (!data) return <div>No feedback analysis found.</div>;

  // Preprocess for summary: Build per-question statistics
  const attempts = data.attempts || [];
  const totalAttempts = data.totalattempts || 0;

  // Map: questionId -> {questionName, answerMap: answer -> { users:[], count: number } }
  const questionStats: Record<
    string,
    {
      questionName: string;
      answerMap: Record<string, { users: string[]; count: number }>;
    }
  > = {};
  const questionOrder: string[] = [];

  attempts.forEach((attempt: any) => {
    (attempt.responses || []).forEach((resp: any) => {
      const questionId = String(resp.id);
      const questionName = resp.name;
      if (!questionStats[questionId]) {
        questionStats[questionId] = {
          questionName,
          answerMap: {},
        };
        questionOrder.push(questionId);
      }
      const answer = (resp.printval || "").trim();
      if (!questionStats[questionId].answerMap[answer]) {
        questionStats[questionId].answerMap[answer] = { users: [], count: 0 };
      }
      questionStats[questionId].answerMap[answer].users.push(attempt.fullname);
      questionStats[questionId].answerMap[answer].count += 1;
    });
  });

  const distinctUsers = new Set(attempts.map((a: any) => a.userid)).size;
  const totalQuestions = Object.keys(questionStats).length;

  // Responsive: chunk questions into rows of 3
  function chunkArray(arr: string[], size: number) {
    const out: string[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      out.push(arr.slice(i, i + size));
    }
    return out;
  }
  const chartRows = chunkArray(questionOrder, 3);

  // Outer wrapper ensures both chart and details use same max width
  return (
    <div
      className="hbr-css__container-card"
      data-col-span="xl"
      data-tablet-col-span="xl"
      style={{
        width: "100%",
        maxWidth: "100%", // Or set a px value if you want a hard limit, e.g. 1200
        boxSizing: "border-box",
      }}
    >
      <h4>Feedback Analysis (ID: {feedbackId})</h4>
      <div style={{ marginBottom: 10 }}>
        <strong>Total Survey Attempts:</strong> {totalAttempts}
        <br />
        <strong>Distinct Users:</strong> {distinctUsers}
        <br />
        <strong>Total Questions:</strong> {totalQuestions}
      </div>

      {/* Charts grid */}
      <div
        style={{
          margin: "24px 0 0 0",
          display: "flex",
          flexDirection: "column",
          gap: 24,
        }}
      >
        {chartRows.map((row, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${row.length}, 1fr)`,
              gap: 18,
              width: "100%",
              minWidth: 0,
            }}
          >
            {row.map((qid, j) => {
              const q = questionStats[qid];
              const colorScheme = chartColorSchemes[(i * 3 + j) % chartColorSchemes.length];
              const config = buildHbrBarChartConfig(
                q.questionName,
                q.answerMap,
                totalAttempts,
                colorScheme
              );
              return (
                <div
                  key={qid}
                  style={{
                    background: "#f8fafb",
                    padding: 12,
                    borderRadius: 8,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                    width: "100%",
                    flex: 1,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 8, fontSize: "1em" }}>
                    {q.questionName}
                  </div>
                  <div style={{ flex: 1, minHeight: 120, width: "100%" }}>
                    <HbrBarChart config={config} categorical style={{ width: "100%", height: 160 }} />
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Details: structured, per-question per-user answers */}
      <div
        style={{
          width: "100%",
          maxWidth: "100%", // or same px value as above if you use px
          boxSizing: "border-box",
        }}
      >
        <details style={{ marginTop: 24 }}>
          <summary style={{ cursor: "pointer", fontWeight: 500 }}>
            Show individual responses (table)
          </summary>
          <div
            style={{
              overflowX: "auto",
              marginTop: 12,
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <table
              style={{
                borderCollapse: "collapse",
                minWidth: 600,
                width: "100%",
                fontSize: "0.97em",
              }}
            >
              <thead>
              <tr>
                <th
                  style={{
                    padding: "4px 8px",
                    borderBottom: "2px solid #eee",
                    textAlign: "left",
                  }}
                >
                  User
                </th>
                {questionOrder.map((qid) => (
                  <th
                    key={qid}
                    style={{
                      padding: "4px 8px",
                      borderBottom: "2px solid #eee",
                      textAlign: "left",
                    }}
                  >
                    {questionStats[qid].questionName}
                  </th>
                ))}
              </tr>
              </thead>
              <tbody>
              {attempts.map((attempt: any) => {
                // Map: qid -> answer
                const responseMap: Record<string, string> = {};
                (attempt.responses || []).forEach((resp: any) => {
                  responseMap[String(resp.id)] = resp.printval || "";
                });
                return (
                  <tr key={attempt.id}>
                    <td
                      style={{
                        padding: "4px 8px",
                        borderBottom: "1px solid #eee",
                        fontWeight: 500,
                      }}
                    >
                      {attempt.fullname}
                    </td>
                    {questionOrder.map((qid) => (
                      <td
                        key={qid}
                        style={{
                          padding: "4px 8px",
                          borderBottom: "1px solid #eee",
                          background: responseMap[qid]
                            ? "#f4fbfa"
                            : "#f9f9f9",
                          color: responseMap[qid] ? "#111" : "#999",
                        }}
                      >
                        {decodeHtmlEntities(responseMap[qid]) || (
                          <span style={{ color: "#ccc" }}>—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
        </details>
      </div>
    </div>
  );
}